import { useRef, useState, useEffect } from 'react';
import clsx from 'clsx';
import { Dropdown, DropdownItem } from '../Dropdown';
import { Table } from '@/components/Elements';
import { twMerge } from 'tailwind-merge';
import { Paginator } from 'primereact/paginator';
import { useTableOperation } from '@/features/explore/api/tableOperation';
import { TableSkeleton } from '@/features/insights/components/loaders/right_side_bar/TableSkeleton';
import { orderBy } from 'lodash';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';

export const TableWithPagination = ({
  itemsPerPage = 2,
  items = [],
  pageRangeDisplayed = 2,
  onPageChange = () => {},
  marginPagesDisplayed = 2,
  className,
  rowClassName,
  headerClassName,
  columnClass,
  displayShowButton = true,
  columns,
  style,
  onRowClick,
  tablePageClassName,
  checkBoxOnChange,
  tableWrapperClassName,
  checkBoxValue,
  rowWrapperClassName,
  showPaginateCentre = false,
  from,
  msg,
  tableRowCount,
  setPaginatedTable,
}) => {
  const [itemOffset, setItemOffset] = useState(0);
  const [localItems, setLocalItems] = useState([]);
  const [checkedState, setCheckedState] = useState([]);
  const [currentPageNo, setCurrentPageNo] = useState(1);
  const [totalRowCount, setTotalRowCount] = useState(tableRowCount);
  const [actualTableRowCount, setActualTableRowCount] = useState(tableRowCount);
  const [actualItems, setActualItems] = useState([]);
  useEffect(() => {
    if (items.length > 0) {
      setLocalItems(items);
      setActualItems(items);
      !tableRowCount && setTotalRowCount(items.length);
    }
  }, [items]);
  const [curItemsPerPage, setCurItemsPerPage] = useState(itemsPerPage);
  const [globalPageNo, setGlobalPageNo] = useState(1);
  const [operation, setOperation] = useState('pagination');
  const endOffset = itemOffset + curItemsPerPage;
  const currentItems =
    totalRowCount > 0 ? localItems.slice(itemOffset, endOffset) : [];
  const pageCount = Math.ceil(totalRowCount / curItemsPerPage);
  const handlePageClick = (event) => {
    setCurrentPageNo(event.page + 1);
    const newOffset = (event.page * curItemsPerPage) % totalRowCount;
    setItemOffset(newOffset);
    onPageChange(event);
  };
  const pageRanges = [5];
  for (let i = 1; i * 20 < totalRowCount - 1; i++) {
    pageRanges.push(20 * i);
  }
  pageRanges.push(totalRowCount);
  const handleItemsPerPage = (event) => {
    setCurItemsPerPage(event.target.innerText);
  };

  const tableOps = useRef({
    conversationId: msg?.conversationId,
    messageId: msg?.id,
    name: msg?.displayName,
    operation: [],
  });
  const agentOperation = useTableOperation({
    config: { enabled: false },
  });

  async function handleTableOps(operation) {
    let localOperation = tableOps.current.operation;
    let exists = localOperation.findIndex(
      (ops) => ops.name === operation.name && ops.field === operation.field
    );
    if (exists === -1) {
      if (operation?.value) {
        operation?.value.length > 0 && localOperation.push(operation);
      } else {
        localOperation.push(operation);
      }
    } else {
      if (operation?.value && operation?.value.length === 0) {
        localOperation.splice(exists, 1);
      } else {
        if (operation.name === 'sort') {
          localOperation.splice(exists, 1);
          localOperation.push(operation);
        } else {
          localOperation[exists] = operation;
        }
      }
    }
    setOperation('operation');
    let result = {};
    if (actualTableRowCount <= 100) {
      let data = actualItems;
      tableOps.current.operation.forEach((ops) => {
        if (ops.name === 'sort') {
          data = orderBy(
            data,
            ops.field,
            ops.type === 'ascending' ? 'asc' : 'desc'
          );
        } else if (ops.name === 'filter' && ops.value.length > 0) {
          data = data.filter((cD) => ops.value.includes(cD[ops.field]));
        }
      });
      setItemOffset(0);
      setLocalItems(data);
      result.count = data.length;
    } else {
      result = await agentOperation.mutateAsync(tableOps.current);
    }
    setTotalRowCount(result?.count);
    setCurrentPageNo(1);
    setGlobalPageNo(1);
  }
  async function removeSort(columnName) {
    let localOperation = tableOps.current.operation;
    let exists = localOperation.findIndex(
      (ops) => ops.field === columnName && ops.name === 'sort'
    );

    localOperation.splice(exists, 1);

    setOperation('operation');
    let result = {};
    if (actualTableRowCount <= 100) {
      let data = actualItems;
      tableOps.current.operation.forEach((ops) => {
        if (ops.name === 'sort') {
          data = orderBy(
            data,
            ops.field,
            ops.type === 'ascending' ? 'asc' : 'desc'
          );
        } else if (ops.name === 'filter' && ops.value.length > 0) {
          data = data.filter((cD) => ops.value.includes(cD[ops.field]));
        }
      });
      setItemOffset(0);
      setLocalItems(data);
      result.count = data.length;
    } else {
      result = await agentOperation.mutateAsync(tableOps.current);
    }
    setTotalRowCount(result?.count);
    setCurrentPageNo(1);
    setGlobalPageNo(1);
  }
  useEffect(() => {
    if (agentOperation?.data?.data && operation !== 'pagination') {
      setItemOffset(0);
      setLocalItems(agentOperation?.data?.data);
    }
  }, [agentOperation?.data?.data]);
  async function handleFetchNewRecords(pageNumber) {
    let loperation = {
      name: 'pagination',
      page_number: pageNumber,
      page_size: 100,
    };
    setOperation('pagination');
    let newData = await agentOperation.mutateAsync({
      ...tableOps.current,
      operation: [...tableOps.current.operation, loperation],
    });
    let data = localItems.concat(newData.data);
    setLocalItems(data);
    setGlobalPageNo(pageNumber);
  }
  useEffect(() => {
    if (
      !agentOperation.isLoading &&
      currentPageNo !== 1 &&
      localItems.length / 10 - currentPageNo <= 4 &&
      !(localItems.length > globalPageNo * 100) &&
      localItems.length < totalRowCount
    ) {
      handleFetchNewRecords(globalPageNo + 1);
    }
  }, [currentPageNo]);
  const refT = useRef();

  useEffect(() => {
    if (
      pageCount > 1 &&
      setPaginatedTable &&
      (!agentOperation.isLoading || operation === 'pagination')
    ) {
      setPaginatedTable(true);
    } else {
      setPaginatedTable && setPaginatedTable(false);
    }
  }, [pageCount, agentOperation, operation]);
  return (
    <>
      {agentOperation.isLoading && currentItems.length === 0 ? (
        <TableSkeleton rows={10} columns={columns.length} />
      ) : !agentOperation.isLoading || operation === 'pagination' ? (
        columns && (
          <div
            className={twMerge(
              tableWrapperClassName,
              clsx(from === 'explore' && 'rounded-lg overflow-hidden')
            )}
            style={{
              ...(from === 'metricsTable' &&
                localItems.length === 1 && {
                  height: '100%',
                }),
            }}
          >
            <Table
              operation={operation}
              handleTableOps={handleTableOps}
              agentOperation={agentOperation}
              tableOps={tableOps}
              msg={msg}
              localItems={localItems}
              checkedState={checkedState}
              setCheckedState={setCheckedState}
              setLocalItems={setLocalItems}
              completeData={items}
              from={from}
              ref={refT}
              style={style}
              columns={columns}
              rows={currentItems}
              onRowClick={onRowClick}
              rowWrapperClassName={rowWrapperClassName}
              checkBoxOnChange={checkBoxOnChange}
              headerClassName={
                headerClassName ? headerClassName : 'bg-brand-ai-100'
              }
              columnClass={twMerge(
                'text-white tracking-normal text-brand-ai-200 scrollbar-hide text-xs py-2 font-medium',
                columnClass
              )}
              rowClassName={twMerge(
                'font-normal text-xs border px-1 py-2',
                rowClassName
              )}
              isDataSource={false}
              key={`${currentItems.length}-${endOffset}`}
              checkBoxValue={checkBoxValue}
              currentPageNo={currentPageNo}
              actualTableRowCount={actualTableRowCount}
              removeSort={removeSort}
            />
          </div>
        )
      ) : (
        <TableSkeleton rows={10} columns={columns.length} />
      )}
      {!agentOperation.isLoading || operation === 'pagination'
        ? pageCount > 1 && (
            <div className={clsx('w-full pt-4', 'bg-transparent', className)}>
              <div
                className={clsx(
                  'flex',
                  `${
                    showPaginateCentre
                      ? 'justify-center'
                      : from === 'explore'
                      ? 'justify-start'
                      : 'justify-end'
                  } items-center`,
                  tablePageClassName
                )}
              >
                {/* <div
            className={clsx("text-sm text-t-medium")}
          >{`${currentItems.length} of ${items.length} items`}</div> */}
                <Paginator
                  first={itemOffset}
                  rows={curItemsPerPage}
                  totalRecords={totalRowCount}
                  onPageChange={handlePageClick}
                  pt={{
                    root: twMerge(
                      'flex gap-2 py-0',
                      from === 'explore' && 'px-2 bg-transparent'
                    ),
                    pages: 'flex gap-2',
                    pagebutton: ({ context }) => ({
                      className: twMerge(
                        'text-xs m-0 min-w-7 w-7 h-7 text-brand-ai-200',
                        context.active &&
                          'rounded-md bg-brand-ai-50 bg-opacity-60 font-medium z-0'
                      ),
                    }),
                  }}
                  template={{
                    layout: 'PrevPageLink PageLinks NextPageLink',
                    PrevPageLink: (options) => {
                      return (
                        <button
                          type="button"
                          className={twMerge(
                            options.className,
                            options.disabled
                              ? 'text-brand-ai-gray-100'
                              : 'hover:text-brand-ai-hover-100 text-brand-ai-100',
                            'gap-1 text-xs z-0'
                          )}
                          onClick={options.onClick}
                          disabled={options.disabled}
                        >
                          <CaretLeft size={14} />
                          {'Prev'}
                        </button>
                      );
                    },
                    NextPageLink: (options) => {
                      return (
                        <button
                          type="button"
                          className={twMerge(
                            options.className,
                            options.disabled
                              ? 'text-brand-ai-gray-100'
                              : 'hover:text-brand-ai-hover-100 text-brand-ai-100',
                            'gap-1 text-xs z-0'
                          )}
                          onClick={options.onClick}
                          disabled={options.disabled}
                        >
                          {'Next'}
                          <CaretRight size={14} />
                        </button>
                      );
                    },
                  }}
                />
                {displayShowButton && (
                  <div className={clsx('flex gap-2 text-sm items-center')}>
                    Show
                    <Dropdown
                      size="sm"
                      title={`${curItemsPerPage} Rows`}
                      headerClasses="text-brand-primary-400 hover:bg-brand-gray-100 text-sm"
                    >
                      {pageRanges.map((pageRange) => (
                        <DropdownItem
                          children={pageRange}
                          onClick={(event) => handleItemsPerPage(event)}
                        />
                      ))}
                    </Dropdown>
                  </div>
                )}
              </div>
            </div>
          )
        : null}
    </>
  );
};
